import _ from 'lodash';
import moment from 'moment';
import { getBeatDataForRange } from '@/shared/lib';

// `holterBeat` is a raw record from Store's IndexedDB beat table (typed `any` there —
// see Store/dbBeatData.ts's BeatDataRecord), mutated in place with sliced number arrays.
export type IHolterBeat = {
  start: string | number | Date;
  stop: string | number | Date;
  beats?: number[];
  hesBeatStatus?: number[];
  beatChannels?: number[];
  beatHeatMapIds?: number[];
  beatTemplateIds?: number[];
  beatEpochs?: number[];
  [key: string]: any;
};

export type IProcessedHolterBeats = {
  beats: number[];
  hesBeatStatus: number[];
  beatEpochs: number[];
  beatChannels: number[];
  beatHeatMapIds: number[];
  beatTemplateIds: number[];
};

// Hàm tách các nhịp biên dựa trên thời gian bắt đầu và kết thúc sự kiện
// `samplingFrequency` is passed by every call site but was never part of the original
// signature (nor used in the body) — declared here to match the real call shape without
// changing behaviour.
/**
 * Trims a beat block in place so only the beats inside the requested window survive. Finds the first epoch at or after start and the last at or before stop, clamps both indexes to the array bounds, and slices beats, hesBeatStatus, beatChannels, beatHeatMapIds, beatTemplateIds and beatEpochs to that span.
 *
 * @param start - Window start; null leaves the leading edge untouched.
 * @param stop - Window stop; null leaves the trailing edge untouched.
 * @param holterBeat - Beat block mutated in place.
 * @param samplingFrequency - Sampling rate accepted for symmetry with the callers, unused by the trim.
 * @returns Nothing.
 */
const extractBorderBeats = (
  start: number | Date | null,
  stop: number | Date | null,
  holterBeat: IHolterBeat,
  samplingFrequency?: number,
) => {
  const {
    beats = [],
    hesBeatStatus = [],
    beatChannels = [],
    beatHeatMapIds = [],
    beatEpochs = [],
    beatTemplateIds = [],
  } = holterBeat;

  let startIndex = start ? _.findIndex(beatEpochs, (beat) => beat >= +start) : 0;
  let stopIndex = stop ? _.findLastIndex(beatEpochs, (beat) => beat <= +stop) + 1 : beats.length;

  startIndex = Math.max(0, startIndex);
  stopIndex = Math.min(beats.length, stopIndex);

  holterBeat.beats = _.slice(beats, startIndex, stopIndex);
  holterBeat.hesBeatStatus = _.slice(hesBeatStatus, startIndex, stopIndex);
  holterBeat.beatChannels = _.slice(beatChannels, startIndex, stopIndex);
  holterBeat.beatHeatMapIds = _.slice(beatHeatMapIds, startIndex, stopIndex);
  holterBeat.beatTemplateIds = _.slice(beatTemplateIds, startIndex, stopIndex);
  holterBeat.beatEpochs = _.slice(beatEpochs, startIndex, stopIndex);
};

// Hàm chuyển đổi các nhịp từ epoch sang thời gian tương đối
/**
 * Converts a beat block's absolute epochs into sample offsets measured from a strip start. Subtracts start from each beatEpochs entry and scales the millisecond difference by the sampling frequency.
 *
 * @param start - Strip start the offsets are measured from.
 * @param holterBeat - Beat block supplying beatEpochs.
 * @param samplingFrequency - Sampling rate used to turn milliseconds into samples.
 * @returns The beat positions as sample indexes relative to start.
 */
const convertEpochsToRelativeTime = (
  start: number | Date,
  holterBeat: IHolterBeat,
  samplingFrequency: number,
): number[] => {
  const { beatEpochs = [] } = holterBeat;
  return _.map(beatEpochs, (epoch) => ((epoch - +start) * samplingFrequency) / 1000);
};

/**
 * Flattens the Holter beat blocks overlapping an event window into one set of strip-relative arrays. Clones and date-normalises the blocks, orders them by start, keeps those overlapping the window, trims the first and last block to the window edges, then flat-maps relative positions alongside the status, epoch, channel, heat-map and template arrays, retrying once when no beats came out.
 *
 * @param filter - Window and payload: start, stop, an optional samplingFrequency defaulting to 1000, and the beat blocks in data.
 * @param retryCount - Recursion depth guard; the retry only runs while it is below one.
 * @returns The merged strip-relative beat arrays for the window.
 */
const processHolterBeats = (
  filter: {
    start: number | string | Date;
    stop: number | string | Date;
    samplingFrequency?: number;
    data?: IHolterBeat[];
  },
  retryCount = 0,
): IProcessedHolterBeats => {
  const { start, stop, samplingFrequency = 1000, data = [] } = filter;
  let count = 0;
  const eventStart = new Date(start);
  const eventStop = new Date(stop);
  const clonedData = _.cloneDeep(data);

  const mappedBeats = clonedData.map((beat) => ({
    ...beat,
    start: new Date(beat.start),
    stop: new Date(beat.stop),
  }));

  const orderedBeats = _.orderBy(mappedBeats, 'start', 'asc');

  const filteredBeats = orderedBeats.filter(
    (beat) => beat.start <= eventStop && beat.stop >= eventStart,
  );

  if (filteredBeats.length) {
    const [firstBeat, lastBeat] = [_.first(filteredBeats), _.last(filteredBeats)];

    if (filteredBeats.length === 1) {
      extractBorderBeats(eventStart, eventStop, firstBeat!, samplingFrequency);
    } else {
      extractBorderBeats(eventStart, null, firstBeat!, samplingFrequency);
      extractBorderBeats(null, eventStop, lastBeat!, samplingFrequency);
    }
  } else {
    console.log('Could not find any beats', { start, stop });
  }

  if (!filteredBeats.length && count < 1) {
    count += 1;
    return processHolterBeats({
      start,
      stop,
      samplingFrequency,
      data,
    });
  }

  const result: IProcessedHolterBeats = {
    beats: _.flatMap(filteredBeats, (beat) =>
      convertEpochsToRelativeTime(eventStart, beat, samplingFrequency),
    ),
    hesBeatStatus: _.flatMap(filteredBeats, 'hesBeatStatus'),
    beatEpochs: _.flatMap(filteredBeats, 'beatEpochs'),
    beatChannels: _.flatMap(filteredBeats, 'beatChannels'),
    beatHeatMapIds: _.flatMap(filteredBeats, 'beatHeatMapIds'),
    beatTemplateIds: _.flatMap(filteredBeats, 'beatTemplateIds'),
  };

  if (_.isEmpty(result.beats) && retryCount < 1) {
    console.log('Retrying processHolterBeats...');
    return processHolterBeats(filter, retryCount + 1);
  }

  return result;
};

// Hàm kiểm tra và xử lý dữ liệu nhịp
/**
 * Resolves the beat data for a strip window and reduces it to strip-relative arrays. Derives the window from momentObject when given, otherwise from the beat epoch widened by customOffset or offset, reads the matching blocks with getBeatDataForRange and runs them through processHolterBeats, retrying once on an empty read and returning null on error.
 *
 * @param beat - Beat anchoring the window; its id, or its start parsed as a date, supplies the epoch.
 * @param offset - Symmetric half-width in milliseconds around the beat epoch.
 * @param ecgDataMap - Loaded ECG map supplying the beat blocks in data and the samplingFrequency.
 * @param momentObject - Explicit startMoment and stopMoment overriding the beat-derived window.
 * @param config - Optional customOffset pair giving asymmetric widths before and after the epoch.
 * @param retryCount - Recursion depth guard; the retry only runs while it is below one.
 * @returns A promise resolving to the processed beat arrays, or null when nothing was found or an error was caught.
 */
const checkAndProcessBeatData = async (
  {
    beat,
    offset,
    ecgDataMap,
    momentObject = {} as { startMoment?: string | number; stopMoment?: string | number },
    config = {} as { customOffset?: [number, number] },
  }: {
    beat?: { id?: number; start: string | number };
    offset?: number;
    ecgDataMap: { data: IHolterBeat[]; samplingFrequency: number };
    momentObject?: { startMoment?: string | number; stopMoment?: string | number };
    config?: { customOffset?: [number, number] };
  },
  retryCount = 0,
): Promise<IProcessedHolterBeats | null> => {
  try {
    const { customOffset } = config;
    // pre-existing: startMoment/stopMoment stay unassigned (and the `!` below lies)
    // when momentObject, customOffset and offset are all absent.
    let startMoment: number;
    let stopMoment: number;

    if (!_.isEmpty(momentObject)) {
      startMoment = moment(momentObject.startMoment).valueOf();
      stopMoment = moment(momentObject.stopMoment).valueOf();
    } else {
      const { samplingFrequency } = ecgDataMap;
      let epoch = beat?.id ? beat.id : new Date(beat!.start).getTime();
      const dividedNumber = 1000 / samplingFrequency;
      epoch = Math.floor(epoch / dividedNumber) * dividedNumber;

      if (!_.isEmpty(customOffset)) {
        startMoment = moment(epoch - customOffset![0]).valueOf();
        stopMoment = moment(epoch + customOffset![1]).valueOf();
      } else if (offset) {
        startMoment = moment(epoch - offset).valueOf();
        stopMoment = moment(epoch + offset).valueOf();
      }
    }

    const beatDataDB = await getBeatDataForRange(startMoment!, stopMoment!, ecgDataMap.data);

    if (beatDataDB?.length) {
      // beat data
      return processHolterBeats({
        start: startMoment!,
        stop: stopMoment!,
        samplingFrequency: ecgDataMap.samplingFrequency,
        data: beatDataDB,
      });
    }

    // Retry 1 lần nếu beatDataDB null hoặc rỗng
    if ((!beatDataDB || !beatDataDB.length) && retryCount < 1) {
      console.log('Retrying checkAndProcessBeatData...');
      return checkAndProcessBeatData(
        {
          beat,
          offset,
          ecgDataMap,
          momentObject,
          config,
        },
        retryCount + 1,
      );
    }

    return null;
  } catch (error) {
    console.error('Error in validateAndProcessBeatData:', error);
    return null;
  }
};

export { checkAndProcessBeatData };
